import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../context/LanguageContext';
import { fetchShotHistory } from '../../services/api';
import { formatShortClock, formatDateTime, formatClock } from './alarmFormat';
import './alarm.css';

/* 알림센터 목록 드로어 + 알람 상세 드로어 — PNG 시안「알림센터 목록 드로어」「알람 상세 드로어」.
 * 화면 오른쪽에서 열리는 460px 패널. 데이터는 AlarmNotificationProvider 가 props 로 준다.
 *
 * 데이터 출처·한계
 * - 목록·상세 기본 정보: /dashboard/alarms/recent (설비·공장·코드·유형·심각도·메시지·발생 시각·상태)
 * - 상세 "발생 지표": 그 설비의 최근 1샷(/machine/shot-history?limit=1) 의 최대사출압·사출완료 위치(쿠션)·사이클타임.
 *   알람 시점의 값이 아니라 지금 값이라 "최근 샷 기준" 을 표기한다.
 * - 시안의 "설정 63 → 실측 58 MPa", "영향: 미성형 위험" 같은 원인 분석은 저장된 데이터가 없어 메시지·코드·유형만 보여 준다.
 * - 확인(ACK)·음소거는 서버 API 가 없어 이 브라우저에만 저장된다(안내 문구 표시). */

const SEVERITIES = ['CRITICAL', 'WARNING', 'INFO'];
const isNum = (v) => v != null && !Number.isNaN(Number(v));
const num = (v, d = 1) => (isNum(v) ? Number(v).toFixed(d) : '—');

function Badge({ severity }) {
  return <span className={`al-badge al-badge-${severity.toLowerCase()}`}>{severity}</span>;
}

/* ---- 알림센터 목록 ---- */
function AlarmListDrawer({ recentAlarms, counts, isAcked, acknowledge, onClose, onOpenDetail, t }) {
  const navigate = useNavigate();
  const [onlyUnacked, setOnlyUnacked] = useState(false);
  const [severity, setSeverity] = useState('ALL');

  const list = useMemo(() => recentAlarms.filter((a) =>
    (!onlyUnacked || !isAcked(a)) && (severity === 'ALL' || a.severity === severity)
  ), [recentAlarms, onlyUnacked, severity, isAcked]);

  const tr = (k, p) => t(`header.center.${k}`, p);

  return (
    <aside className="al-drawer" role="dialog" aria-label={tr('title')}>
      <div className="al-drawer-head">
        <div className="al-drawer-title-row">
          <h2 className="al-drawer-title">{tr('title')}</h2>
          {counts.unacked > 0 && <span className="al-badge al-badge-critical">{tr('unread', { count: counts.unacked })}</span>}
        </div>
        <button type="button" className="al-icon-btn" onClick={onClose} title={t('common.close')} aria-label={t('common.close')}>
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>
      <p className="al-drawer-sub">{tr('subtitle')}</p>

      <div className="al-filters">
        <button type="button" className={`al-chip ${!onlyUnacked ? 'active' : ''}`} onClick={() => setOnlyUnacked(false)}>{tr('all', { count: counts.total })}</button>
        <button type="button" className={`al-chip ${onlyUnacked ? 'active' : ''}`} onClick={() => setOnlyUnacked(true)}>{tr('unreadChip', { count: counts.unacked })}</button>
        <select className="al-select" value={severity} onChange={(e) => setSeverity(e.target.value)} aria-label={tr('severityAll')}>
          <option value="ALL">{tr('severityAll')}</option>
          {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="al-list">
        {list.length === 0 ? <div className="al-empty">{tr('empty')}</div> : list.map((a) => {
          const acked = isAcked(a);
          return (
            <button key={a.key} type="button" className={`al-card al-sev-${a.severity.toLowerCase()}`} onClick={() => onOpenDetail(a)}>
              <div className="al-card-row">
                <span className="al-card-title">{a.equipmentName} · {a.message || a.code}</span>
                {acked
                  ? <span className="al-card-acked"><span className="material-symbols-outlined">check</span>{tr('acked')}</span>
                  : <i className={`al-dot al-dot-${a.severity.toLowerCase()}`} aria-hidden="true" />}
              </div>
              <div className="al-card-desc">{a.code}{a.type ? ` · ${a.type}` : ''}</div>
              <div className="al-card-row al-card-foot">
                <Badge severity={a.severity} />
                <span className="al-card-meta">{a.factoryName || '—'} · {formatShortClock(a.at)}</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="al-drawer-foot">
        <button type="button" className="al-btn-outline" disabled={list.every(isAcked)} onClick={() => acknowledge(list.filter((a) => !isAcked(a)).map((a) => a.key))}>
          {tr('ackAll')}
        </button>
        <button type="button" className="al-link" onClick={() => { onClose(); navigate('/alarm-analysis'); }}>{tr('history')}</button>
      </div>
      <p className="al-drawer-note">{tr('ackNote')}</p>
    </aside>
  );
}

/* ---- 알람 상세 ---- */
function AlarmDetailDrawer({ alarm, recentAlarms, isAcked, acknowledge, muteEquipment, muteUntil, onClose, onOpenList, onOpenDetail, t }) {
  const navigate = useNavigate();
  const [latestShot, setLatestShot] = useState(null);
  const [shotState, setShotState] = useState('loading');
  const tr = (k, p) => t(`header.detail.${k}`, p);

  /* 같은 심각도 알람 사이의 위치 (시안: "CRITICAL 4건 중 1") */
  const sameSeverity = useMemo(() => recentAlarms.filter((a) => a.severity === alarm.severity), [recentAlarms, alarm.severity]);
  const index = sameSeverity.findIndex((a) => a.key === alarm.key);
  const prev = index > 0 ? sameSeverity[index - 1] : null;
  const next = index >= 0 && index < sameSeverity.length - 1 ? sameSeverity[index + 1] : null;

  useEffect(() => {
    if (!alarm.equipmentId) { setShotState('none'); return undefined; }
    let cancelled = false;
    setShotState('loading');
    fetchShotHistory(alarm.equipmentId, 1)
      .then((d) => { if (!cancelled) { setLatestShot(d?.shots?.[0] || null); setShotState(d?.shots?.[0] ? 'ok' : 'none'); } })
      .catch((e) => { console.error('Alarm detail shot load failed:', e); if (!cancelled) setShotState('none'); });
    return () => { cancelled = true; };
  }, [alarm.equipmentId, alarm.key]);

  const acked = isAcked(alarm);
  const mutedUntil = muteUntil(alarm.equipmentId);

  const goEquipment = () => {
    onClose();
    navigate('/machine', { state: { machineData: { id: alarm.equipmentId, name: alarm.equipmentName }, openTab: 'alarm', alarmId: alarm.code } });
  };

  const askAi = () => {
    const question = tr('askQuestion', { equipment: alarm.equipmentName, message: alarm.message || alarm.code, code: alarm.code });
    onClose();
    window.dispatchEvent(new CustomEvent('smi:ask-ai', { detail: { question } }));
  };

  return (
    <aside className={`al-drawer al-detail al-sev-${alarm.severity.toLowerCase()}`} role="dialog" aria-label={alarm.message}>
      {/* 상단 심각도 바: n건 중 i · 이전 / 다음 · 전체 목록 */}
      <div className={`al-detail-bar al-bar-${alarm.severity.toLowerCase()}`}>
        <span className="material-symbols-outlined al-detail-bar-icon">{alarm.severity === 'INFO' ? 'info' : 'warning'}</span>
        <span className="al-detail-bar-text">{tr('position', { sev: alarm.severity, n: sameSeverity.length, i: Math.max(1, index + 1) })}</span>
        <span className="al-detail-bar-nav">
          <button type="button" className="al-bar-link" disabled={!prev} onClick={() => prev && onOpenDetail(prev)}>{tr('prev')}</button>
          <button type="button" className="al-bar-link al-bar-link-strong" disabled={!next} onClick={() => next && onOpenDetail(next)}>{tr('next')}</button>
          <span className="al-bar-sep" />
          <button type="button" className="al-bar-link al-bar-link-accent" onClick={onOpenList}>{tr('list')}</button>
        </span>
      </div>

      <div className="al-detail-body">
        <div className="al-drawer-head">
          <div className="al-drawer-title-row">
            <h2 className="al-drawer-title al-detail-equipment">{alarm.equipmentName}</h2>
            <Badge severity={alarm.severity} />
          </div>
          <button type="button" className="al-icon-btn" onClick={onClose} title={t('common.close')} aria-label={t('common.close')}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <h3 className="al-detail-message">{alarm.message || alarm.code}</h3>
        <p className="al-detail-meta">
          {alarm.factoryName || '—'} · {alarm.timeAgo ? tr('timeAgoAt', { time: formatDateTime(alarm.at), ago: alarm.timeAgo }) : formatDateTime(alarm.at)}
        </p>

        {/* 원인 — 저장된 분석이 없어 알람 메시지·코드·유형을 보여 준다 */}
        <h4 className="al-section-title">{tr('cause')}</h4>
        <div className={`al-cause al-cause-${alarm.severity.toLowerCase()}`}>
          <div className="al-cause-headline">{alarm.message || alarm.code}</div>
          <div className="al-cause-detail">{tr('codeLabel', { code: alarm.code || '—' })}{alarm.type ? ` · ${tr('typeLabel', { type: alarm.type })}` : ''}</div>
          <div className="al-cause-detail al-cause-time">{formatClock(alarm.at)}{alarm.timeAgo ? ` · ${alarm.timeAgo}` : ''}</div>
        </div>

        {/* 발생 지표 — 최근 1샷 기준 */}
        <div className="al-section-row">
          <h4 className="al-section-title">{tr('indicators')}</h4>
          <span className="al-section-note">{tr('indicatorNote')}</span>
        </div>
        <div className="al-indicators">
          <div className="al-indicator"><span>{tr('maxPressure')}</span><b>{shotState === 'loading' ? '…' : num(latestShot?.injMaxPress, 1)}<small>MPa</small></b></div>   {/* shot-history.injMaxPress */}
          <div className="al-indicator"><span>{tr('cushion')}</span><b>{shotState === 'loading' ? '…' : num(latestShot?.injEndPosi, 1)}<small>mm</small></b></div>        {/* shot-history.injEndPosi */}
          <div className="al-indicator"><span>{tr('cycleTime')}</span><b>{shotState === 'loading' ? '…' : num(latestShot?.cycleTime, 1)}<small>s</small></b></div>          {/* shot-history.cycleTime */}
        </div>

        {/* 빠른 조치 */}
        <h4 className="al-section-title">{tr('quick')}</h4>
        <div className="al-actions">
          <button type="button" className="al-btn al-btn-primary" disabled={acked} onClick={() => acknowledge(alarm.key)}>{acked ? tr('acked') : tr('ack')}</button>
          <button type="button" className="al-btn al-btn-primary" disabled={!!mutedUntil || !alarm.equipmentId} onClick={() => muteEquipment(alarm.equipmentId, 30)}>
            {mutedUntil ? tr('muted', { time: formatShortClock(mutedUntil) }) : tr('mute')}
          </button>
          <button type="button" className="al-btn al-btn-danger al-btn-wide" disabled={!alarm.equipmentId} onClick={goEquipment}>{tr('goEquipment')}</button>
          <button type="button" className="al-btn al-btn-primary al-btn-wide" onClick={askAi}><span className="al-star">✦</span>{tr('askAi')}</button>
        </div>
      </div>

      <div className="al-drawer-foot al-detail-foot">
        <span className="al-detail-id">{tr('alarmId', { id: alarm.alarmId != null ? alarm.alarmId : '—' })}</span>
        <button type="button" className="al-link" onClick={() => { onClose(); navigate('/alarm-analysis'); }}>{tr('history')}</button>
      </div>
      <p className="al-drawer-note">{t('header.center.ackNote')}</p>
    </aside>
  );
}

function AlarmDrawers({ drawer, recentAlarms, counts, isAcked, acknowledge, muteEquipment, muteUntil, onClose, onOpenList, onOpenDetail }) {
  const { t } = useTranslation();

  // ESC 로 닫기
  useEffect(() => {
    if (!drawer) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawer, onClose]);

  if (!drawer) return null;
  const alarm = drawer.type === 'detail' ? recentAlarms.find((a) => a.key === drawer.key) : null;
  if (drawer.type === 'detail' && !alarm) {
    /* 폴링으로 목록에서 밀려난 알람이면 목록으로 되돌린다 */
    return ReactDOM.createPortal(
      <div className="al-overlay" onClick={onClose}>
        <div onClick={(e) => e.stopPropagation()}>
          <AlarmListDrawer recentAlarms={recentAlarms} counts={counts} isAcked={isAcked} acknowledge={acknowledge} onClose={onClose} onOpenDetail={onOpenDetail} t={t} />
        </div>
      </div>,
      document.body
    );
  }

  return ReactDOM.createPortal(
    <div className="al-overlay" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}>
        {drawer.type === 'list'
          ? <AlarmListDrawer recentAlarms={recentAlarms} counts={counts} isAcked={isAcked} acknowledge={acknowledge} onClose={onClose} onOpenDetail={onOpenDetail} t={t} />
          : (
            <AlarmDetailDrawer
              alarm={alarm}
              recentAlarms={recentAlarms}
              isAcked={isAcked}
              acknowledge={acknowledge}
              muteEquipment={muteEquipment}
              muteUntil={muteUntil}
              onClose={onClose}
              onOpenList={onOpenList}
              onOpenDetail={onOpenDetail}
              t={t}
            />
          )}
      </div>
    </div>,
    document.body
  );
}

export default AlarmDrawers;
